import type React from 'react';
import { useState } from 'react';

type FormInputProps = {
  type: 'text' | 'email' | 'password';
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  error?: string;
  rightElement?: React.ReactNode;
  name?: string;
  // 新增属性用于控制自动填充行为
  autoComplete?: string;
  id?: string;
  // 新增 tabIndex 属性用于控制 tab 顺序
  tabIndex?: number;
};

// Eye icon components
const EyeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export const FormInput = ({
  type,
  value,
  onChange,
  placeholder,
  error,
  rightElement,
  name,
  autoComplete,
  id,
  tabIndex,
}: FormInputProps) => {
  const [showPassword, setShowPassword] = useState(false);

  // 确定实际的输入类型
  const inputType = type === 'password' && showPassword ? 'text' : type;

  // 根据输入类型和传入的autoComplete属性确定最终的autocomplete值
  const getAutoCompleteValue = (): string => {
    // 如果明确指定了autoComplete，优先使用
    if (autoComplete) {
      return autoComplete;
    }

    // 根据类型和name属性智能设置autocomplete
    switch (type) {
      case 'email':
        return 'email';
      case 'password':
        // 根据name属性判断是新密码还是当前密码
        if (name === 'newPassword' || name === 'confirmPassword') {
          return 'new-password';
        }
        return 'current-password';
      case 'text':
        // 对于用户名/邮箱输入框
        if (name === 'email' || name === 'username') {
          return 'username';
        }
        // 对于验证码等特殊字段，禁用自动填充
        if (name === 'verificationCode' || name === 'code') {
          return 'off';
        }
        // 其他文本输入框
        return 'on';
      default:
        return 'on';
    }
  };

  // 创建密码可见性切换按钮
  const passwordToggle =
    type === 'password' ? (
      <button
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        className="text-[#B6BCD1] hover:text-[#326BFB] focus:outline-none"
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        {showPassword ? <EyeIcon /> : <EyeOffIcon />}
      </button>
    ) : null;

  // 确定右侧显示的元素
  const displayRightElement = rightElement || passwordToggle;

  // 生成唯一的ID（如果没有提供的话）
  const inputId = id || name || `input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="">
      {/* 输入容器，使用相对定位来放置右侧元素 */}
      <div className="relative">
        <input
          type={inputType}
          name={name}
          id={inputId}
          autoComplete={getAutoCompleteValue()}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`h-[36px] w-full rounded-[4px] bg-[#FAFAFA] px-[18px] py-[10px] text-[14px] text-[#000000] placeholder-[#B6BCD1] focus:ring-0 ${
            error
              ? 'outline outline-1 outline-[#CF0000] focus:outline-[#CF0000]'
              : 'outline outline-1 outline-[#C5C5C5] focus:outline-[#326BFB]'
          } ${displayRightElement ? 'pr-[50px]' : ''}`}
          tabIndex={tabIndex}
        />
        {displayRightElement && (
          <div className="absolute right-[18px] top-1/2 flex -translate-y-1/2 items-center gap-[12px]">
            {displayRightElement}
          </div>
        )}
      </div>
      {/* 错误消息容器：预留一行高度，内容溢出时展开 */}
      <div className="mt-[4px] min-h-[18px] w-full">
        {error && (
          <p className="break-words text-[12px] font-normal leading-[18px] text-[#FA4E3E]">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};
